import React, { ReactElement, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';

import TextInput from '~/components/inputs/TextInput';
import Subtext from '~/components/titles/Subtext';
import Button from '~/components/buttons/Button';
import Announcement from '~/components/Announcement';
import Gap from '~/components/flow/Gap';
import COLORS from '~/constants/colors';
import Break from '~/components/flow/Break';
import Code from '~/components/flow/Code';
import { FONT_SIZES, FONT_WEIGHTS } from '~/constants/typography';
import VARIANTS from '~/constants/variants';

import { encode } from '../../../services';
import { validateImageDims } from '../../../utils';
import {
  MIME_TYPES,
  MIN_IMAGE_SIZE,
  MAX_IMAGE_SIZE,
} from '../../../constants/media';
import Display from './Display';

const DEFAULT_ENCODE_INFO = {
  message: '',
  original: undefined as File | undefined,
};

function Form(): ReactElement {
  const [fail, setFail] = useState('');
  const [result, setResult] = useState<File>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    resetField,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: DEFAULT_ENCODE_INFO });
  const validate = async (file) => {
    if (!Object.values(MIME_TYPES).includes(file.type)) return false;

    const hasCorrectDims = await validateImageDims(file);

    return hasCorrectDims;
  };
  const messageOpts = { required: true };
  const originalOpts = { required: true, validate };

  // Rendering
  const defaultPBLabel = 'Select image';
  const [pBLabel, setPBLabel] = useState(defaultPBLabel);
  const [picked, setPicked] = useState(false);

  // Handlers
  const startPicking = () => fileInputRef.current?.click();
  const handlePicking = async () => {
    const file = fileInputRef.current?.files?.[0];
    const canEncode = file && (await validate(file));

    if (!canEncode) {
      setPBLabel(defaultPBLabel);
      setPicked(false);
      setError('original', { type: 'manual' });
      resetField('original', { keepError: true });
    } else {
      setPBLabel(file.name);
      setPicked(true);
      clearErrors('original');
      setValue('original', file);
    }
  };
  const onEncode = (encodeInfo) => {
    return new Promise<void>((resolve) => {
      setFail('');
      encode(encodeInfo.message, encodeInfo.original)
        .then((encoded) => {
          setResult(encoded);
          setPBLabel(defaultPBLabel);
          setPicked(false);
          reset();
          if (fileInputRef.current) fileInputRef.current.value = '';
        })
        .catch((e) => {
          setFail(e.response.data.error.message);
          resolve();
        });
    });
  };

  // Styling
  const formStyle = {
    display: 'inline-block',
    width: '90%',
  };
  const textInputStyle = {
    width: '100%',
    margin: '0 -0.4em',
    fontSize: FONT_SIZES.action,
  };
  const encodeButtonStyle = {
    width: '63%',
  };
  const announcementStyle = {
    fontWeight: FONT_WEIGHTS.bold,
    backgroundColor: COLORS.cashmere,
  };
  const pBStyle0 = {
    width: 'calc(100% + 0.8em)',
    margin: '0 -0.4em',
    color: COLORS.doveGray,
    borderColor: COLORS.waikawaGray,
    backgroundColor: COLORS.white75,
    fontWeight: FONT_WEIGHTS.light,
    textAlign: 'left' as const,
  };
  const pBStyle1 = {
    ...pBStyle0,
    backgroundColor: COLORS.kournikova,
    fontWeight: FONT_WEIGHTS.normal,
  };

  return (
    <>
      {fail && (
        <>
          <Announcement style={announcementStyle}>{fail}</Announcement>
          <Break />
        </>
      )}
      <form style={formStyle}>
        <div>
          <Button
            variant={VARIANTS.neutral}
            label={pBLabel}
            onClick={startPicking}
            onTouchEnd={startPicking}
            style={picked ? pBStyle1 : pBStyle0}
          />
          <input
            {...register('original', originalOpts)}
            type="file"
            hidden
            ref={fileInputRef}
            onChange={handlePicking}
          />
          {errors.original && (
            <Subtext color={COLORS.mandy}>Invalid format or dimensions</Subtext>
          )}
        </div>
        <Gap />
        <Subtext>
          Auri doesn&apos;t store any files. Please resize large images if
          it&apos;s taking too long. I encourage you to test decoding before
          sharing. Images must be in <Code>.png</Code> or <Code>.jpg</Code>{' '}
          format. Both dimensions must be between <b>{MIN_IMAGE_SIZE}</b> and{' '}
          <b>{MAX_IMAGE_SIZE}</b> pixels.
        </Subtext>
        <Break />
        <div>
          <TextInput
            {...register('message', messageOpts)}
            placeholder="Identifying message"
            style={textInputStyle}
          />
          {errors.message && (
            <Subtext color={COLORS.mandy}>Message required</Subtext>
          )}
        </div>
        <Break />
        <Button
          label="Encode"
          loadingLabel="Encoding..."
          loading={isSubmitting}
          onClick={handleSubmit(onEncode)}
          onTouchEnd={handleSubmit(onEncode)}
          style={encodeButtonStyle}
        />
      </form>
      <Display result={result} setResult={setResult} />
    </>
  );
}

export default Form;
